import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, LogOut, Mail, FileText, RefreshCw, AlertTriangle, Paperclip, Download, CalendarClock, MessageCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { adminListEnquiries, adminGetEnquiry, adminExportEnquiries, adminNoFundingStats, fileUrl } from "../../lib/api";
import { LOGOUT } from "../../constants/testIds";

const ROUTE_LABEL = { A: "A · Hearing", B: "B · Existing case", C: "C · Guidance", D: "D · Appeal" };
const NoFundingStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    adminNoFundingStats().then(setS).catch((e) => console.error("[enquiries] no-funding stats failed", e));
  }, []);
  if (!s) return null;
  return (
    <div className="border border-border bg-white px-5 py-4 mb-5 flex flex-wrap items-baseline gap-x-8 gap-y-2" data-testid="no-funding-stats">
      <p className="text-sm text-brand-darkgrey">Closed form — no funds / need Legal Aid or pro bono</p>
      <p className="text-sm"><span className="font-serif text-2xl text-brand-black" data-testid="no-funding-last30">{s.last_30_days}</span> last 30 days</p>
      <p className="text-sm"><span className="font-serif text-2xl text-brand-black" data-testid="no-funding-total">{s.total}</span> all time</p>
      <p className="text-xs text-muted-foreground" data-testid="no-funding-split">Urgent form {s.urgent} · Funding page {s.funding}</p>
    </div>
  );
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function EnquiriesDashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [route, setRoute] = useState("");
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [returning, setReturning] = useState("");
  const [entryMode, setEntryMode] = useState("");
  const [adviser, setAdviser] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (route) params.route = route;
      if (channel) params.channel = channel;
      if (status) params.status = status;
      if (returning) params.returning = returning;
      if (entryMode) params.entry_mode = entryMode;
      if (adviser) params.adviser = adviser;
      const d = await adminListEnquiries(params);
      setItems(d.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, route, channel, status, returning, entryMode, adviser]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    try {
      const d = await adminGetEnquiry(id);
      setDetail(d);
    } catch (e) {
      setDetail(null);
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (route) params.route = route;
      if (channel) params.channel = channel;
      if (status) params.status = status;
      if (returning) params.returning = returning;
      if (entryMode) params.entry_mode = entryMode;
      if (adviser) params.adviser = adviser;
      const blob = await adminExportEnquiries(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[enquiries] CSV export failed", e);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <p className="font-serif text-xl text-brand-black">Family Court Guide — Enquiries</p>
          <div className="flex items-center gap-4">
            <Link to="/admin/chat-leads" className="text-sm text-brand-darkgrey hover:text-brand-gold inline-flex items-center gap-1" data-testid="nav-chat-leads">
        <MessageCircle className="h-4 w-4" /> Chat leads
      </Link>
      <Link to="/admin/availability" className="text-sm text-brand-darkgrey hover:text-brand-gold inline-flex items-center gap-1" data-testid="nav-availability">
              <CalendarClock className="h-4 w-4" /> Availability
            </Link>
            <Link to="/admin/email-templates" className="text-sm text-brand-darkgrey hover:text-brand-gold inline-flex items-center gap-1" data-testid="nav-email-templates" style={{ display: user?.role === "admin" ? undefined : "none" }}>
              <Mail className="h-4 w-4" /> Email templates
            </Link>
            <span className="text-xs text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" onClick={logout} data-testid={LOGOUT.button} className="text-brand-darkgrey hover:text-brand-black rounded-sm">
              <LogOut className="h-4 w-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <NoFundingStats />
        <div className="flex flex-wrap gap-3 mb-5 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone or reference"
              data-testid="enquiries-search" className="pl-9 rounded-sm focus-visible:ring-brand-gold" />
          </div>
          <select value={route} onChange={(e) => setRoute(e.target.value)} data-testid="filter-route" className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
            <option value="">All routes</option>
            <option value="A">Route A</option><option value="B">Route B</option><option value="C">Route C</option><option value="D">Route D</option>
          </select>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="filter-channel" className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
            <option value="">All sources</option>
            <option value="Facebook">Facebook</option><option value="Instagram">Instagram</option>
            <option value="TikTok">TikTok</option><option value="our website">Website</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
            <option value="">All statuses</option>
            <option value="submitted">Submitted</option><option value="in_progress">In progress</option><option value="linked">Linked</option>
          </select>
          <select value={returning} onChange={(e) => setReturning(e.target.value)} data-testid="filter-returning" className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
            <option value="">All clients</option>
            <option value="true">Returning / paid only</option>
          </select>
          <select value={entryMode} onChange={(e) => setEntryMode(e.target.value)} data-testid="filter-entry-mode" className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
            <option value="">All methods</option>
            <option value="chat">Web chat</option>
            <option value="form">Website form</option>
          </select>
          <select value={adviser} onChange={(e) => setAdviser(e.target.value)} data-testid="filter-adviser" className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
            <option value="">All advisers</option>
            <option value="robin">Robin Lane</option>
            <option value="amanda">Amanda Rex</option>
            <option value="brian">Brian Hudson</option>
          </select>
          <Button variant="outline" onClick={load} data-testid="enquiries-refresh" className="rounded-sm border-brand-darkgrey">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="enquiries-export" className="rounded-sm border-brand-darkgrey">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>

        <div className="border border-border bg-white overflow-x-auto" data-testid="enquiries-table">
          <table className="w-full text-sm">
            <thead className="bg-brand-grey/50 text-left">
              <tr className="text-xs uppercase tracking-wider text-brand-darkgrey">
                <th className="px-4 py-3">Reference</th><th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Route</th><th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Consultation</th>
                <th className="px-4 py-3">Adviser</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Received</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No enquiries found.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} onClick={() => openDetail(it.id)} data-testid={`enquiry-row-${it.reference}`}
                  className="border-t border-border hover:bg-brand-grey/30 cursor-pointer">
                  <td className="px-4 py-3 font-medium text-brand-gold">
                    <span className="flex items-center gap-2">
                      {it.urgent && <AlertTriangle className="h-4 w-4 text-brand-danger" />} {it.reference}
                    </span>
                    {it.returning && (
                      <span className="mt-1 inline-block text-[10px] uppercase tracking-wider bg-brand-black text-white px-1.5 py-0.5 rounded-sm" data-testid={`returning-badge-${it.reference}`}>
                        Returning
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{it.name || "—"}<div className="text-xs text-muted-foreground">{it.email}</div></td>
                  <td className="px-4 py-3">{ROUTE_LABEL[it.route] || it.route || "—"}</td>
                  <td className="px-4 py-3">
                    {it.channel || "—"}
                    {it.entry_mode === "chat" && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider bg-brand-gold/25 text-brand-black px-1.5 py-0.5 rounded-sm" data-testid={`chat-badge-${it.reference}`}>Chat</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {it.consultation === "paid" ? (
                      <span className="text-brand-black">{(it.appointment_type || "Paid").replace("Paid ", "Paid · ")}</span>
                    ) : it.consultation === "free" ? (
                      <span className="text-brand-darkgrey">Free</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`adviser-cell-${it.reference}`}>
                    {it.adviser ? (
                      <span className="flex items-center gap-1.5 text-brand-black">
                        {it.adviser}
                        {it.adviser_assigned === false && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-brand-danger/15 text-brand-danger px-1.5 py-0.5 rounded-sm" title="The client's CRM contact was not assigned to this adviser — adviser SMS may not have sent" data-testid={`unassigned-badge-${it.reference}`}>
                            <AlertTriangle className="h-3 w-3" /> Unassigned
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 capitalize">{(it.status || "").replace("_", " ")}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(it.submitted_at || it.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">{items.length} shown (most recent first, max 300).</p>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="enquiry-detail">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <FileText className="h-5 w-5 text-brand-gold" />
              {detail?.reference || "Enquiry"} {detail?.urgent && <span className="text-brand-danger text-sm">· URGENT</span>}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Full enquiry details, source and uploaded files.</DialogDescription>
          </DialogHeader>
          {detail?.loading ? (
            <p className="text-sm text-muted-foreground py-6">Loading…</p>
          ) : detail ? (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Source:</span> {detail.source?.channel || "—"}</div>
                <div><span className="text-muted-foreground">Landing:</span> {detail.source?.landing_page || "—"}</div>
                <div><span className="text-muted-foreground">Referrer:</span> {detail.source?.referrer || "—"}</div>
                <div><span className="text-muted-foreground">GHL contact:</span> {detail.ghl_contact_id ? "synced" : "—"}</div>
              </div>

              {detail.appointment && (
                <div className="border border-brand-gold/60 bg-brand-grey/30 p-3 text-sm">
                  <span className="font-semibold">Appointment:</span> {fmt(detail.appointment.start_time)}
                </div>
              )}

              <div className="border border-border">
                <table className="w-full text-sm">
                  <tbody>
                    {(detail.summary_rows || []).map(([label, value]) => (
                      <tr key={label} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 bg-brand-grey/40 text-brand-darkgrey w-2/5 align-top">{label}</td>
                        <td className="px-3 py-2 text-brand-black align-top">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detail.data?.chat_transcript && (
                <div data-testid="detail-chat-transcript">
                  <p className="text-sm font-semibold text-brand-black mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-brand-gold" /> Web chat transcript
                  </p>
                  <pre className="text-xs text-brand-black bg-brand-grey/30 border border-border rounded-sm p-3 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{detail.data.chat_transcript}</pre>
                </div>
              )}

              {detail.files?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-brand-black mb-2 flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-brand-gold" /> Uploaded files
                  </p>
                  <ul className="space-y-1">
                    {detail.files.map((f) => (
                      <li key={f.id}>
                        <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="text-sm text-brand-gold underline underline-offset-2" data-testid={`file-link-${f.id}`}>
                          {f.filename || f.id}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
